import { Alert, AlertProps } from "@texada/ui";
import { DOCUMENT_EXTENSION } from "@texada/ui/src/components/documentviewer/MapViewer";
import { AxiosError } from "axios";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import Big from "big.js";
import * as checkCardType from "credit-card-type";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import {
  get,
  isEmpty,
  isFinite,
  isInteger,
  isNil,
  isObject,
  pick,
  snakeCase,
  toNumber,
  trim,
  isArray,
  head
} from "lodash";
import moment from "moment";
import React from "react";
import {
  ICustomFlaskForm,
  ICustomSRMForm
} from "pages/settings/customFields/form/common";
import {
  ALERT_ERROR_TIMEOUT,
  INITIAL_STATE,
  INSPECTION_PHOTO_URL,
  GLOBAL_ENV,
  ENV_MAP,
  HEADOFFICE_OPTION_VALUE
} from "../constants/AppConstants";
import { getMechanicUrl } from "./urls";
import { Warehouse } from "models";
export * from "./typeHelpers";

export type ReduxState = typeof INITIAL_STATE;

export enum PostingType {
  StandardInvoice = "S",
  CustomerRepair = "R",
  Internal = "I",
  CustomerDamage = "D",
  None = "N"
}

export function parseTime(time): string {
  if (time) {
    return time.replace(/(\d{2})(?=\d)/g, "$1:");
  }
  return time;
}

/** This fuction is to be used for encoding url parameters. */
export function encodeURLParam(url: string) {
  return encodeURIComponent(decodeURIComponent(url));
}

function endWithSlash(url: string) {
  if (url.endsWith("/")) return url;
  return url + "/";
}

export const hasArrayWithValue = (arr: any) => {
  return Array.isArray(arr) && arr.length > 0;
};

export const hasObjectWithValue = (obj: any) => {
  return isObject(obj) && Object.keys(obj).length > 0 && !isArray(obj)
    ? true
    : false;
};

export function getTableLoadUrl({
  ticketUrl,
  page,
  page_size,
  search_text,
  filter_value,
  sorted,
  divisions,
  locations,
  mechanic,
  expand
}) {
  // handle zero indexed page
  let page_num = Math.max(Number.parseInt(page as string, 10), 1);
  let url = new Uri(ticketUrl)
    .addQueryParam("page", page_num)
    .addQueryParam("page_size", page_size);

  if (!ticketUrl.includes("v1")) {
    url.setPath(endWithSlash(url.path()));
  }

  if (search_text !== undefined) {
    url.addQueryParam("query", search_text);
  }
  if (filter_value !== undefined && filter_value.length !== 0) {
    for (let filters of filter_value) {
      if (filters.type) {
        url.addQueryParam(filters.type, filters.value);
      }
    }
  }
  if (sorted !== undefined && sorted.length !== 0) {
    // let sort_val = sorted[0]
    for (let sort_val of sorted) {
      if (sort_val.desc === true) {
        url.addQueryParam("ordering", sort_val.id);
      } else {
        url.addQueryParam("ordering", sort_val.id);
      }
    }
  }

  if (divisions !== undefined) {
    url.addQueryParam(
      "division_auth_id__in",
      divisions.map(d => d.warehouse.id).join(",")
    );
  }
  if (locations !== undefined) {
    url.addQueryParam(
      "location_auth_id__in",
      locations.map(l => l.warehouse.id).join(",")
    );
  }
  if (isInteger(mechanic)) {
    url.addQueryParam("mechanics", mechanic);
  } else if (Array.isArray(mechanic)) {
    url.addQueryParam("mechanics", mechanic.join(","));
  }
  if (expand !== undefined && expand.length > 0) {
    url.addQueryParam("expand", expand.join(","));
  }
  url.setPath(url.path().replace(/\/$/, ""));
  return url;
}

export function getTableLoadFlaskUrl(
  TICKET_URL,
  page,
  page_size,
  search_text,
  filter_value,
  sorted
) {
  // handle zero indexed page
  let page_num = Number.parseInt(page, 10) + 1;
  let url = new Uri(TICKET_URL)
    .addQueryParam("page", page_num)
    .addQueryParam("per_page", page_size);
  if (search_text !== undefined) {
    url.addQueryParam("search", search_text);
  }
  if (filter_value !== undefined && filter_value.length !== 0) {
    for (let filters of filter_value) {
      url.addQueryParam(filters.type, filters.value);
    }
  }
  if (sorted !== undefined && sorted.length !== 0) {
    // let sort_val = sorted[0]
    for (let sort_val of sorted) {
      if (sort_val.sort === "desc") {
        url.addQueryParam("sort", `-${sort_val.colId}`);
      } else {
        url.addQueryParam("sort", `+${sort_val.colId}`);
      }
    }
  }
  return url;
}

export function getLocationLoadUrl(
  TICKET_URL,
  divisions,
  locations,
  pageSize,
  query,
  filterOptions
) {
  let url = new Uri(TICKET_URL);
  if (divisions !== undefined) {
    url.addQueryParam(
      "division_auth_id__in",
      divisions.map(d => d.warehouse.id).join(",")
    );
  }
  if (locations !== undefined) {
    url.addQueryParam(
      "location_auth_id__in",
      locations.map(l => l.warehouse.id).join(",")
    );
  }
  if (pageSize !== undefined) {
    url.addQueryParam("page_size", pageSize);
  }
  if (query !== undefined) {
    url.addQueryParam("query", query);
  }
  if (pageSize !== undefined) {
    url.addQueryParam("page_size", pageSize);
  }
  if (filterOptions !== undefined) {
    if (Array.isArray(filterOptions)) {
      if (filterOptions.length > 0) {
        for (let filters of filterOptions) {
          // check the filters
          if (filters.type === "status") {
            url.addQueryParam("status", filters.value);
          } else if (filters.type === "priority") {
            url.addQueryParam("priority", filters.value);
          }
        }
      }
    }
  }
  return url;
}

export enum BILLING_TYPE {
  NORMAL = "N",
  SPLIT = "S",
  WARRANTY = "W"
}

export const BILLING_OPTIONS = [
  { label: "Normal", value: BILLING_TYPE.NORMAL },
  { label: "Split", value: BILLING_TYPE.SPLIT },
  { label: "Warranty", value: BILLING_TYPE.WARRANTY }
];

export const POSTING_OPTIONS: { label: string; value: PostingType }[] = [
  { label: "Standard Invoice", value: PostingType.StandardInvoice },
  { label: "Customer Damage", value: PostingType.CustomerDamage },
  { label: "Customer Repair", value: PostingType.CustomerRepair },
  { label: "Internal Work Order", value: PostingType.Internal }
];

export function getPostingType(type: PostingType) {
  let postingType = "N/A";
  switch (type) {
    case PostingType.StandardInvoice:
      postingType = "standard_invoice";
      break;
    case PostingType.CustomerDamage:
      postingType = "customer_damage";
      break;
    case PostingType.CustomerRepair:
      postingType = "customer_repair";
      break;
    case PostingType.Internal:
      postingType = "internal_workorder";
      break;
  }
  return i18n.t(postingType);
}

export function getBillingType(type: BILLING_TYPE) {
  let billingType = "N/A";
  switch (type) {
    case BILLING_TYPE.NORMAL:
      billingType = "normal";
      break;
    case BILLING_TYPE.SPLIT:
      billingType = "split";
      break;
    case BILLING_TYPE.WARRANTY:
      billingType = "warranty";
      break;
  }
  return i18n.t(billingType);
}

export enum DOC_STATUS_CODE {
  ON_CONTRACT = "C",
  ON_DELIVERY = "D",
  OFF_RENTAL = "F",
  INVENTORY_TRANSFER = "I",
  ON_LEASE = "L",
  ON_SALES_ORDER = "O",
  ON_PICKUP = "P",
  ON_RESERVATION = "R",
  ON_SAVED_WORKSHEET = "S",
  ON_WORKORDER = "W",
  ON_PICKUP_EXCHANGE = "X",
  ON_CLOSED_PICKUP = "CP",
  EXCHANGED = "E"
}

export const DOC_STATUS_CODE_OPTIONS = [
  { label: "On Contract", value: DOC_STATUS_CODE.ON_CONTRACT },
  { label: "On Delivery", value: DOC_STATUS_CODE.ON_DELIVERY },
  { label: "Off Rental", value: DOC_STATUS_CODE.OFF_RENTAL },
  { label: "On Transfer", value: DOC_STATUS_CODE.INVENTORY_TRANSFER },
  { label: "On Lease", value: DOC_STATUS_CODE.ON_LEASE },
  { label: "On Sales Order", value: DOC_STATUS_CODE.ON_SALES_ORDER },
  { label: "On Pickup", value: DOC_STATUS_CODE.ON_PICKUP },
  { label: "On Reservation", value: DOC_STATUS_CODE.ON_RESERVATION },
  { label: "On Saved Worksheet", value: DOC_STATUS_CODE.ON_SAVED_WORKSHEET },
  { label: "On Work Order", value: DOC_STATUS_CODE.ON_WORKORDER },
  { label: "On Pickup Exchange", value: DOC_STATUS_CODE.ON_PICKUP_EXCHANGE },
  { label: "On Closed Pickup Ticket", value: DOC_STATUS_CODE.ON_CLOSED_PICKUP },
  { label: "Available", value: "A" }
];

export const USER_STATUS = {
  ACTIVE: true,
  INACTIVE: false
};

export const WO_CONVERTED_STATUS = {
  CONVERTED: "converted",
  ESTIMATE: "estimate"
};

export const WO_STATUS = {
  CREATED: 0,
  ASSIGNED: 1,
  IN_PROGRESS: 2,
  CLOSED: 3,
  COMPLETE: 4,
  PAUSED: 7
};

export const WoStatusMap = {
  [WO_STATUS.CREATED]: "Created",
  [WO_STATUS.ASSIGNED]: "Assigned",
  [WO_STATUS.IN_PROGRESS]: "In Progress",
  [WO_STATUS.CLOSED]: "Closed",
  [WO_STATUS.COMPLETE]: "Complete",
  [WO_STATUS.PAUSED]: "Paused"
};

export const WO_OPTIONS = Object.values(WO_STATUS).map(status => ({
  label: WoStatusMap[status],
  value: status
}));

export function getWoStatus(statusId, { upperCase = true } = {}) {
  const woStatus = WoStatusMap[Number(statusId)] ?? "";
  return upperCase ? woStatus.toUpperCase() : woStatus;
}

export const MECHANIC_STATUS = {
  IN_PROGRESS: 1,
  PAUSED: 2,
  ASSIGNED: 3,
  COMPLETED: 4,
  UNASSIGNED: 5
};

export enum TICKET_STATUS {
  CREATED = 0,
  ASSIGNED = 1,
  IN_PROGRESS = 2,
  CLOSED = 3,
  COMPLETE = 4,
  LOADING = 5,
  UNLOADING = 6,
  PAUSED = 7,
  CANCELED = 8
}

export const TicketStatusMap = {
  [TICKET_STATUS.CREATED]: i18n.t("created"),
  [TICKET_STATUS.ASSIGNED]: i18n.t("assigned"),
  [TICKET_STATUS.IN_PROGRESS]: i18n.t("in_progress"),
  [TICKET_STATUS.CLOSED]: i18n.t("closed"),
  [TICKET_STATUS.COMPLETE]: i18n.t("complete"),
  [TICKET_STATUS.LOADING]: i18n.t("loading"),
  [TICKET_STATUS.UNLOADING]: i18n.t("unloading"),
  [TICKET_STATUS.PAUSED]: i18n.t("paused"),
  [TICKET_STATUS.CANCELED]: i18n.t("canceled")
};

export function getTicketStatus(statusId, { upperCase = true } = {}) {
  const ticketStatus = TicketStatusMap[Number(statusId)] ?? "";
  return upperCase ? ticketStatus.toUpperCase() : ticketStatus;
}

export const DAMAGE_LIST_STATUS = {
  CREATED: "created",
  REVIEWED: "reviewed"
};



export const PRIORITY = {
  CRITICAL: 1,
  MODERATE: 2,
  LOW: 3
};

export const FLAG_STATUS = {
  FLAGGED: "True",
  UNFLAGGED: "False"
};

export const PRIORITIES = [
  {
    label: "Low",
    value: 3,
    className: "low"
  },
  {
    label: "Moderate",
    value: 2,
    className: "moderate"
  },
  {
    label: "Critical",
    value: 1,
    className: "critical"
  }
];

export const INVOICE_SOURCE = {
  COUNTER: "",
  CYCLE_BILL: "B",
  CONTRACT: "C",
  FAST_TRACK: "F",
  LEASE: "L",
  SALES_ORDER: "O",
  AR_CREDIT: "R",
  SPLIT: "S",
  WORK_ORDER: "W",
  CYLINDER: "Y"
};

export enum ASSET_STATUS {
  OnRent = "O",
  OffRent = "F",
  Sold = "S",
  Returned = "R"
}

export enum ASSET_TYPE {
  Rental = "R",
  Sales = "S",
  CustomerOwned = "C"
}

export enum METER_TYPE {
  METERED = "M",
  OLD_METERED = "Y",
  UNMETERED = "N",
  BLADED = "B",
  NONE = ""
}

export enum PART_LABOUR_SERVICE_ACTION {
  ADD = "ADD",
  UPDATE = "UPDATE",
  DELETE = "DELETE"
}

export enum TRANSACTION_TYPE {
  RENTAL = "R",
  NON_SERIALIZED = "S",
  SERVICE = "V",
  GROUP = "G"
}

export enum TRANSACTION_MODE {
  ADD = "A",
  CHANGE = "C",
  DELETE = "D"
}

export enum MAINTENANCE_TYPE {
  ByRentalDays = "1",
  ByDays = "2",
  ByDate = "3",
  ByMeterUnit = "4",
  ByMeterReading = "5"
}

export enum STAGING_STATUS {
  CREATED = "created",
  ASSIGNED = "assigned",
  IN_PROGRESS = "in-progress",
  STAGED = "staged",
  HONORED = "honored"
}

export const StagingStatusMap = {
  [STAGING_STATUS.CREATED]: i18n.t("created"),
  [STAGING_STATUS.ASSIGNED]: i18n.t("assigned"),
  [STAGING_STATUS.IN_PROGRESS]: i18n.t("in_progress"),
  [STAGING_STATUS.STAGED]: i18n.t("staged"),
  [STAGING_STATUS.HONORED]: i18n.t("honored")
};

export enum LABOR_STATUS {
  APPROVED = "approved",
  PENDING = "pending",
  REJECTED = "rejected"
}

export const LaborStatusMap = {
  [LABOR_STATUS.APPROVED]: i18n.t("approved"),
  [LABOR_STATUS.PENDING]: i18n.t("pending"),
  [LABOR_STATUS.REJECTED]: i18n.t("rejected")
};

export const Labor_OPTIONS = Object.values(LABOR_STATUS).map(status => ({
  label: LaborStatusMap[status],
  value: status
}));

export const MAP_MAINTENANCE_TYPE_VALUE_TO_LABEL = Object.keys(
  MAINTENANCE_TYPE
).reduce((newObj, item) => {
  return {
    ...newObj,
    [MAINTENANCE_TYPE[item]]: snakeCase(item)
  };
}, {});

export enum MAINTENANCE_SCHEDULE_TYPE {
  ONE = "Y",
  NO_MAINTENANCE = "N",
  MULTIPLE_MAINTENANCE = "M"
}

export const ASSET_STATUS_OPTIONS = [
  { label: "On Rent", value: ASSET_STATUS.OnRent },
  { label: "Off Rent", value: ASSET_STATUS.OffRent },
  { label: "Sold", value: ASSET_STATUS.Sold },
  { label: "Returned", value: ASSET_STATUS.Returned }
];

export const ASSET_TYPE_LABELS = {
  [ASSET_TYPE.Rental]: "Rental",
  [ASSET_TYPE.Sales]: "Sales"
};

export const authCheck = () => {
  window.location.href = "/";
  localStorage.clear();
  localStorage.setItem(
    "login-error",
    "Your session has expired. Please login again."
  );
};

export function getPriority(priorityCode: string | number) {
  priorityCode = toNumber(priorityCode);

  let priority = "";
  switch (priorityCode) {
    case 1:
      priority = "Critical";
      break;
    case 2:
      priority = "Moderate";
      break;
    case 3:
      priority = "Low";
      break;
  }

  return priority;
}

export const NumberFormat = new Intl.NumberFormat(
  navigator.language.includes("fr") ? "fr-ca" : "en-us"
);

export const CurrencyFormat = (() => {
  try {
    return new Intl.NumberFormat(
      navigator.language.includes("fr") ? "fr-ca" : "en-us",
      {
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol"
      }
    );
  } catch {
    return new Intl.NumberFormat(
      navigator.language.includes("fr") ? "fr-ca" : "en-us",
      {
        style: "currency",
        currency: "USD"
      }
    );
  }
})();

export const DATE_FORMAT = "DD/MM/YYYY";

export const sleep = (milliseconds?: number) =>
  new Promise(resolve => setTimeout(resolve, milliseconds));

export function isValidDiscount(discount) {
  discount = toNumber(discount);
  return discount >= 0 && discount <= 100 && isFinite(discount);
}

export const isValidNumber = number => {
  // disallow scientific notation
  if (typeof number === "string" && number.indexOf("e") !== -1) {
    return false;
  }

  try {
    new Big(number);
    return true;
  } catch (e) {
    return false;
  }
};

export const getCardType = cardNumber => {
  if (typeof checkCardType === "function") {
    const cardType = checkCardType(cardNumber);
    return cardType.length === 0 ? null : cardType;
  }
  return null;
};

export const getConvertCardTypeToSrmCardIssuer = type => {
  /* Defined by SRM */
  if (type === "bank") {
    return "B"; // This one is for the bank.
  }

  if (type === "interac") {
    return "I"; // This one is for the interac.
  }

  var CardTypes = checkCardType.types;
  if (type === CardTypes.VISA) {
    return "V";
  } else if (type === CardTypes.MASTERCARD) {
    return "M";
  } else if (type === CardTypes.AMERICAN_EXPRESS) {
    return "A";
  } else if (type === CardTypes.DINERS_CLUB) {
    return "D";
  } else if (type === CardTypes.DISCOVER) {
    return "S";
  }

  return "";
};

function sumBig(accumulator: Big, currVal: number | string | Big): Big {
  try {
    accumulator = accumulator.plus(Big(currVal));
  } catch { }
  return accumulator;
}

export function calculateSum(...numbers): Big {
  return numbers.filter(isValidNumber).reduce(sumBig, Big(0));
}

export function calculateSumByProperty(values, propertyName): Big {
  if (hasArrayWithValue(values)) {
    return values.reduce(
      (accumulator, currVal) => sumBig(accumulator, currVal[propertyName]),
      Big(0)
    );
  }
  return Big(0);
}

export function formatSrmAddress(
  srmAddress: Partial<
    Record<
      | "address1"
      | "address2"
      | "city"
      | "postal_zip"
      | "provState"
      | "province"
      | "postalZip"
      | "prov_state",
      string
    >
  >
) {
  if (isEmpty(srmAddress)) return "";

  const {
    address1,
    address2,
    city,
    postalZip,
    postal_zip,
    provState,
    province,
    prov_state,
    zip_postal_code,
    state_province
  } = srmAddress;

  let address = [
    address1,
    address2,
    city,
    postalZip || postal_zip || zip_postal_code,
    provState || province || prov_state || state_province
  ]
    .map(v => trim(v))
    .filter(v => !isEmpty(v))
    .join(", ");

  return address;
}

export function formatAddressMultiline(
  srmAddress: Partial<
    Record<
      | "address1"
      | "address2"
      | "city"
      | "postalZip"
      | "postal_zip"
      | "provState"
      | "province",
      string
    >
  >
) {
  const {
    address1,
    address2,
    city,
    postalZip,
    postal_zip,
    provState,
    province
  } = srmAddress;

  let address = [];

  if (address1 !== "") address.push(address1);
  if (address2 !== "") address.push(address2);

  let details = [];
  if (city !== "") details.push(city);
  if (provState || province !== "") details.push(provState || province);
  if (postalZip !== "") details.push(postalZip || postal_zip);
  if (details.length !== 0) address.push(details.join(", "));

  return address;
}

export enum PAGE_NUMBER {
  ZERO = 0,
  ONE = 1,
  FIVE = 5,
  TEN = 10,
  FIFTEEN = 15,
  TWENTY = 20,
  TWENTY_FIVE = 25,
  FIFTY = 50,
  HUNDRED = 100,
  FIVE_HUNDRED = 500,
  THOUSAND = 1000
}

export const MAX_ASSET_DESCRIPTION_LENGTH = 30;
export const MAX_TEXT_LIMIT = 64;

export const toAbsoluteLink = (link: string) =>
  link.includes("//") ? link : `//${link}`;

export function buildSRMNote(note) {
  const MAX_COMMENT_LENGTH = 60;

  note = note.split("");
  let comments = [];

  function buildComments(note) {
    const subStr = note.slice(0, MAX_COMMENT_LENGTH);
    const lastSpaceIndex = subStr.lastIndexOf(" ");

    let comment = [];
    if (lastSpaceIndex === -1 || note.length <= MAX_COMMENT_LENGTH) {
      comment = subStr;

      note = note.slice(subStr.length, note.length);
    } else {
      comment = subStr.slice(0, lastSpaceIndex);

      note = note.slice(lastSpaceIndex + 1, note.length);
    }

    comments.push(comment.join(""));

    if (note.length !== 0) {
      buildComments(note);
    }
  }

  buildComments(note);
  return comments;
}

export function getInvoiceTotal(invoice) {
  if (Array.isArray(invoice.payments)) {
    const paymentsAmount = invoice.payments
      .map(i => i.amount)
      .reduce(sumBig, Big(0));
    return Number(Big(invoice.totalAmount).minus(paymentsAmount));
  }

  return invoice.onAcct === "N" ? invoice.totalAmount : invoice.onAcctAmount;
}

export function handleSpace(data, keys, toValue) {
  keys.forEach(e => {
    if (data[e] !== undefined && isEmpty(data[e].trim())) {
      data[e] = toValue;
    }
  });
  return data;
}

export function handleSnakeCase(data) {
  let snakeData = {};
  Object.keys(data).forEach(e => {
    snakeData[snakeCase(e)] = data[e];
  });
  return snakeData;
}

interface IApiError {
  message: string;
  error: string;
  errorMsg: string;
}
export function getApiErrorMsg<T extends IApiError = any>(
  error: AxiosError<T>
): string {
  if (!error?.response?.data) {
    console.error(error);
    return "";
  }
  let errorRes = error?.response?.data;

  if (errorRes && typeof errorRes === "string") {
    try {
      errorRes = JSON.parse(errorRes);
    } catch (err) {
      if (typeof errorRes === "string") {
        return errorRes;
      }
    }
  }
  if (errorRes?.message) {
    return errorRes.message;
  }
  if (errorRes?.Message) {
    return errorRes.Message;
  }
  if (errorRes?.error) {
    return get(errorRes, "error.error", "") || errorRes.error;
  }
  if (errorRes?.errorMsg) {
    if (typeof errorRes.errorMsg === "string") {
      try {
        errorRes = JSON.parse(errorRes.errorMsg);
        if (errorRes?.validator) {
          return `${errorRes.field}: ${errorRes.msg}`;
        }
      } catch (err) {
        return errorRes.errorMsg;
      }
    }
    return errorRes.errorMsg;
  }
  return "";
}

export function showApiErrorAlert({
  message,
  error,
  alertProps = {
    timeout: ALERT_ERROR_TIMEOUT
  }
}: {
  message: string;
  error: AxiosError;
  alertProps?: Omit<AlertProps, "message">;
}) {
  const errorMsg = getApiErrorMsg(error);

  Alert.error({
    message: (
      <>
        {message}
        {errorMsg && <div className="api-error">({errorMsg})</div>}
      </>
    ),
    ...alertProps
  });
}

export const countFilters = ({ filters = {} }) => {
  const combineFilters = allFilter => {
    return Object.keys(allFilter).reduce((newObj, item) => {
      return {
        ...newObj,
        [item]: get(allFilter, item, "")
      };
    }, {});
  };

  const calculateTotal = allFilter =>
    Object.keys(allFilter).reduce((total, item) => {
      const value = allFilter[item];
      const isValidArray =
        Array.isArray(value) &&
        value.length &&
        value.every(v => v !== "" && v !== null);
      const isValidString =
        typeof value === "string" &&
        String(value).trim() !== "" &&
        value !== null;
      return isValidArray || isValidString ? total + 1 : total;
    }, 0);

  const allFilters = combineFilters(filters);
  const totalFilters = calculateTotal(allFilters);
  return totalFilters;
};

/**
 * @link https://github.com/Texada/srm_rest_api/blob/master/srm_rest_api/exceptions.py
 */
export enum FlaskErrorCode {
  INVALID_REQUEST_DATA = 100,
  INTEGRITY_ERROR = 104,
  INVALID_RESOURCE_ID = 105
}

export function toShipInfo(site: any): Models.CustomerInfo {
  return pick(
    {
      ...site,
      postalZip: site.postal_zip || site.postalZip,
      provState: site.province || site.provState
    },
    [
      "address1",
      "address2",
      "city",
      "name",
      "phone",
      "postalZip",
      "provState",
      "siteNumber",
      "postal_zip",
      "province"
    ] as (keyof Models.CustomerInfo)[]
  );
}

export function toFlaskShipInfo(site: any): Models.CustomerInfo {
  const address = pick(site, [
    "address1",
    "address2",
    "city",
    "phone",
    "postalZip",
    "provState",
    "siteNumber"
  ] as (keyof Models.CustomerInfo)[]);
  address.customerName = site.name || site.customer_name;
  address.postalZip = site.postalZip || site.postal_zip;
  address.provState = site.provState || site.province || site.prov_state;
  address.siteNumber = site.siteNumber || site.site_number;
  return address;
}

export function isPlayEnv() {
  return (
    window.location.hostname ===
    new Uri(getEnvConstant(GLOBAL_ENV.PLAY_ENVIRONMENT)).host()
  );
}

export enum ADDRESS_KEY {
  ORIGIN = "origin",
  DESTINATION = "destination"
}

export function getTicketFullAddress(
  ticket: Models.BaseTicket | Models.WorkOrder | Models.TicketAddress,
  addressKey: ADDRESS_KEY
) {
  const address1 = ticket[`${addressKey}_addr1`];
  const address2 = ticket[`${addressKey}_addr2`];
  const city = ticket[`${addressKey}_city`];
  const prov_state = ticket[`${addressKey}_prov_state`];
  const postal_zip = ticket[`${addressKey}_postal_zip`];
  return `${address1} ${address2} ${city} ${prov_state} ${postal_zip}`;
}

export enum EVENT_KEY {
  ENTER = "Enter",
  ARROW_DOWN = "ArrowDown",
  ARROW_UP = "ArrowUp"
}

/**
 * Removes first element from array that predicate returns truthy for and
 * returns new array.
 */
export function removeFirst<T = any>(
  array: Array<T>,
  predicate: (item: T) => boolean
) {
  const newArray = [...array];
  const index = newArray.findIndex(predicate);
  if (index !== -1) newArray.splice(index, 1);
  return newArray;
}

export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

export enum SrmBoolean {
  True = "Y",
  False = "N",
  Division = "D"
}

export enum CustomerAccountType {
  OnAcount = "Y",
  Cash = "N",
  OnHold = "H"
}

export enum WorkOrderMaintenanceType {
  SINGLE = "Y",
  MULTIPLE = "M",
  NOT_SCHEDULED = "N"
}

export function hasProperty(obj: any, key: string) {
  if (isNil(obj)) return false;
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function formatLocal(date, format) {
  return moment.utc(date).local().format(format);
}

export function generateFileInfoMessage(targetFile, dateFormat) {
  return `Uploaded to this file by ${targetFile.driverName} on ${formatLocal(
    targetFile.createdAt,
    dateFormat
  )}`;
}

export enum WINDOW_EVENT {
  WHEEL = "wheel",
  KEYUP = "keyup",
  RESIZE = "resize",
  STORAGE = "storage",
  UNLOAD = "unload",
  LOGOUT = "logout",
  CLICK = "click"
}

export enum SiteType {
  Billing = "billing",
  Existing = "existing",
  New = "new"
}

export function getFixedValue(value) {
  if (value === 0 || value === 100) {
    return value;
  }

  return parseFloat(value).toFixed(2);
}

export enum PermissionValue {
  VIEW = "view",
  EDIT = "edit",
  CREATE = "create",
  DELETE = "delete"
}

export enum DOCUMENT_TYPE {
  RESERVATION = "reservation",
  CONVERT_RESERVATION = "convert_reservation",
  CONVERT_QUOTE = "convert_quote",
  DELIVERY = "delivery",
  PICKUP = "pickup",
  TASK = "task",
  WORKORDER = "workorder",
  WORKORDER_ESTIMATE = "workorder_estimate",
  CONTRACT = "contract",
  QUOTE = "quote",
  INVOICE = "invoice",
  INSPECTION = "inspection",
  ALL_IN_ONE = "all-in-one",
  CUSTOMER = "customer",
  CUSTOMER_SITE = "customer_site",
  CONTACT = "contact",
  PRODUCT = "product",
  SERVICE = "service",
  ASSET = "asset",
  PART = "part",
  COMMENT = "comment",
  LINE_ITEM_COMMENT = "line_item_comment",
  DAMAGE_WAIVER = "damage_waiver",
  SALES_PERSON = "salesperson",
  DATE_OUT = "date_out",
  ATTACHMENT = "attachment",
  OFF_RENTAL = "off_rental",
  DATE_IN = "date_in",
  EMAIL = "email",
  DEPOSIT = "deposit",
  RENTAL = "rental",
  TAX = "tax",
  RENTAL_DISCOUNT = "rental_discount",
  SALE_DISCOUNT = "sale_discount",
  WORKSHEET = "worksheet",
  SITE = "site",
  RENTAL_QUOTE = "rentalquote"
}

export function formatRole(role: string) {
  let updatedRole = "";
  switch (role) {
    case "create":
      updatedRole = "created";
      break;
    case "edit":
      updatedRole = "edited";
      break;
    case "delete":
      updatedRole = "deleted";
      break;
  }

  return updatedRole;
}

export const generateRandomPassword = () =>
  window.crypto
    .getRandomValues(new BigUint64Array(1))[0]
    .toString(36)
    .toUpperCase() +
  window.crypto.getRandomValues(new BigUint64Array(1))[0].toString(36);

export const getExtension = file => {
  const fileName = get(file, "internal_name", get(file, "name", ""));
  const extension = fileName.substring(fileName.lastIndexOf("."));
  return extension;
};

export const isPDF = file => {
  const extension = getExtension(file);
  return extension.includes(DOCUMENT_EXTENSION.PDF);
};

export const RESPONSE_FILE_TYPE = {
  PDF: "application/pdf",
  MICROSOFT_WORD_OLD: "application/msword",
  MICROSOFT_WORD_NEW:
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  PNG: "image/png",
  JPEG: "image/jpeg",
  JPG: "image/jpeg",
  WEBP: "image/webp",
  XML: "text/xml"
};

export enum FORM_TYPE {
  ADD = "add",
  EDIT = "edit"
}

export const CREDIT_RATING = {
  LOW_RISK: i18n.t("low_risk"),
  HIGH_RISK: i18n.t("high_risk"),
  GOOD_CUSTOMER: i18n.t("good_customer")
};

export const checkCreditRating = ({ creditRating, okToContinue }) => {
  if (okToContinue === SrmBoolean.False) {
    return { creditRating: CREDIT_RATING.HIGH_RISK, showWarning: true };
  }
  if (
    creditRating.allowActivity === "W" ||
    creditRating.allowActivityIfUnpaid === "W"
  ) {
    return { creditRating: CREDIT_RATING.LOW_RISK, showWarning: true };
  }

  return { creditRating: CREDIT_RATING.GOOD_CUSTOMER, showWarning: false };
};

export const damageWaiverOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

export enum companyCurrencyCountryCode {
  CANADA = "C",
  UNITED_STATES = "U"
}

export const createContractOptions = {
  saveAsContract: {
    label: i18n.t("save_as_contract"),
    value: "SAVE_AS_CONTRACT"
  },
  saveDraft: {
    label: i18n.t("save_draft"),
    value: "SAVE_DRAFT"
  },
  deleteDraft: {
    label: i18n.t("delete_draft"),
    value: "DELETE_DRAFT"
  },
  deleteContract: {
    label: i18n.t("delete_contract"),
    value: "DELETE_CONTRACT"
  }
} as const;

export const formatCityByCountryName = (countryName, address) => {
  let city = "";
  if (address) {
    city =
      countryName === address?.countryName ? address?.district : address?.city;
  }
  return city || "";
};

export interface IInventoryDivision {
  address?: {
    address1: string;
    address2: string;
    city: string;
    fax: string;
  };
  check_location?: string;
  damage_waiver?: string;
  defaultDateRng?: number;
  divisionCode?: string;
  divisionName?: string;
  hoursDays?: string;
  isDivision?: string;
  islocation?: string;
  timeOut?: string;
}

export interface ISystemInfo {
  allow_override_quantity: string;
  change_rate_level: string;
  companyName: string;
  company_currency: string;
  company_postal_code_format: string;
  credit_check_continue: string;
  dbID: string;
  enhanced_off_rental: string;
  off_rental_reason_codes: string;
  company_phone_number_format: string;
  allow_override_quantity_on_reservation: string;
}

export interface IAsset {
  adjusted_first_cost: number;
  allow_rental_discount: string;
  alternate_list: number;
  assetOrPart: string;
  availability: {
    availability: string;
    descrption: string;
    location: string;
    locationName: string;
    number: string;
    productType;
    string;
    quantity: number;
  }[];
  averageCodeEach: number;
  barcodes: Array<string>;
  bitmap_image_name: string;
  classDescription: string;
  contractNumbers: string[];
  cost_type: string;
  cubes: number;
  currentMeter: number;
  currentMeterDate: null | string | Date;
  cycleBill: string;
  dateAcquired: string;
  dateOfManufacture: null | string;
  date_counted: null | number;
  date_last_move: string;
  description: string;
  engine_make: string;
  externalResourceUrl: string;
  fuelTypes: Array<any>;
  groupDescription: string;
  height: number;
  ignore_list_price_matrix: string;
  initialMeter: number;
  initialMeterDate: null;
  internetDescription: string;
  inventory: "Y";
  isStoppedForPO: string;
  kitNumber: string;
  landed_cost_factor: 1;
  landed_purchase_order_cost: number;
  last_purchase_order_cost: number;
  length: number;
  license: string;
  license2_expiry_date: null;
  license_expiry_date: null;
  listPrice: number;
  locations: Array<{
    binNumber: string;
    location: string;
    onhand: number;
  }>;
  ltdMeter: number;
  make: string;
  markupPercentage: number;
  meterDigits: number;
  meterType: string;
  meter_reading: number;
  modelNumber: string;
  multipleTags: string;
  notes: string;
  productClass: string;
  productGroup: string;
  quantityOnHand: number;
  quantity_on_contract: number;
  quantity_on_purchase_order: number;
  quantity_on_reservation: number;
  ratebook: number;
  reorderPrice: number;
  replacementCost: number;
  reservation_numbers: Array<any>;
  safetyNotes: Array<any>;
  serial: string;
  showOnWebsite: string;
  sslNumber: string;
  statusCode: string;
  telematicID: string;
  timestamp: string | Date;
  vendorDescription: string;
  vendorNumber: string;
  vendorProductNumber: string;
  vin_number: string;
  warrantyExpiryDate: string;
  weight: number;
  width: number;
  type: number;
  product: string;
}

export enum RESERVATION_STATUS {
  CREATED = "created",
  ASSIGNED = "assigned",
  IN_PROGRESS = "in-progress",
  STAGED = "staged",
  HONORED = "honored"
}

export enum IN_YARD_RETURNS_STATUS {
  CREATED = 1,
  CLOSED = 0
}

export const IN_YARD_RETURN_STATE = {
  OPEN: "N",
  CLOSED: "Y"
} as const;

export enum WORK_ORDER_PAGE_TYPE {
  WORK_ORDER = "workorders",
  ESTIMATE = "estimates"
}

export enum COST_TYPE {
  AVERAGE = "average",
  PROMPT = "prompt"
}

export const getAttributeValueMap = (
  attributes: Models.Attribute[],
  type: string
) =>
  attributes
    ?.filter((item: any) => item?.attribute_type === type)
    ?.reduce((acc, item) => {
      acc[item?.attribute_id] = item?.attribute_values?.map((value: any) => ({
        value: value?.attribute_value_id,
        label: value?.attribute_value_name
      }));
      return acc;
    }, {});

export const fetchInspectionPhotos = async photoUrls => {
  const base64 = await Promise.allSettled(
    photoUrls.map(async photoUrl => {
      try {
        const res = await fleetlogicAPI.get(
          `${INSPECTION_PHOTO_URL}/${encodeURLParam(photoUrl)}/blob`
        );
        return res?.data?.base64;
      } catch (error) {
        console.error(error);
        return null;
      }
    })
  );
  return base64.filter(data => data !== null).map(data => data.value);
};

export const stripHtml = html => {
  // Html tags using regular expression
  const removedHtml = html ? html.replace(/<\/?[^>]+(>|$)/g, "") : "";

  // Dom has a built in cleaner for html tags
  const temporaryElement = document.createElement("div");
  temporaryElement.innerHTML = removedHtml;
  return temporaryElement.textContent || temporaryElement.innerText || "";
};

export type TFieldConfiguration = ICustomSRMForm[keyof ICustomSRMForm] & any;
export const convertSrmToFlaskCustomField = (
  fieldConfiguration: TFieldConfiguration
): ICustomFlaskForm => {
  const field = head(
    fieldConfiguration?.fields
  ) as TFieldConfiguration["fields"];
  const instructions = head(
    field?.instructions
  ) as TFieldConfiguration["instructions"];

  const detail_list = [
    {
      clone_to_new: instructions?.copy,
      required: instructions?.required,
      document_type: instructions?.docType
    }
  ];
  const field_type = field?.type;
  const hint_for_text_type = field?.hint_for_text_type ?? "";
  const select_option_list = field?.selections
    ? field?.selections?.map(option => {
      return {
        select_option: option?.select
      };
    })
    : [];
  const description = field?.prompt;
  const number_of_decimals = field?.decimals ?? 0;
  const number_of_digits = field?.digits ?? 0;

  return {
    description,
    detail_list,
    field_name: fieldConfiguration?.name,
    field_type,
    hint_for_text_type,
    select_option_list,
    number_of_decimals,
    number_of_digits
  };
};

export const checkValidLatitudeLongitude = (lat, lng) => {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);

  if (
    !isNil(latitude) &&
    !isNil(longitude) &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude)
  ) {
    return true;
  }
  return false;
};

export function getEnvConstant(constantKey = "") {
  let host = window.location.hostname;
  if (host.includes("apac")) {
    if (host.includes("play")) {
      // APAC Play env
      return ENV_MAP[`${constantKey}_PLAY_APAC`];
    } else {
      // APAC env
      return ENV_MAP[`${constantKey}_APAC`];
    }
  }

  if (host.startsWith("app.") || host.startsWith("fleetlogic.")) {
    // NA env
    return ENV_MAP[`${constantKey}_NA`];
  }

  if (host.startsWith("play.")) {
    // NA Play env
    return ENV_MAP[`${constantKey}_PLAY_NA`];
  }

  if (host.includes("dev")) {
    // DEV env
    return ENV_MAP[`${constantKey}_DEV`];
  }

  if (host.includes("qa")) {
    // QA env
    return ENV_MAP[`${constantKey}_QA`];
  }

  if (host.includes("fl3")) {
    // UAT env
    return ENV_MAP[`${constantKey}_UAT`];
  }

  return ENV_MAP[constantKey];
}

export enum docType {
  VIEW = "view",
  EDIT = "edit",
  CREATE = "create",
  DELETE = "delete"
}

export const transformGatewayCategories = list => {
  const uniqueCategories = new Set();
  return (
    list?.reduce((acc, category) => {
      if (!uniqueCategories.has(category.cat_id)) {
        uniqueCategories.add(category.cat_id);
        acc.push({
          label: category.cat_name,
          value: category.cat_id,
          displayCategory: category.display_category
        });
      }
      return acc;
    }, []) || []
  );
};

export const getProcessingFeeInformation = (user: any) => {
  const revenueFee = Number(user?.systemInfo?.revenue_fee ?? 0);
  const isRevenueFeeOptional =
    user?.systemInfo?.revenue_fee_optional === SrmBoolean.True;
  const isRevenueFeatureEnabled =
    Boolean(user?.companyFeatures?.credit_card_fee) && revenueFee !== 0;

  return {
    isRevenueFeeOptional,
    isRevenueFeatureEnabled,
    revenueFeeLabel: user?.systemInfo?.revenue_fee_description,
    revenueFeePercentage: Number(Number(revenueFee / 100).toFixed(2))
  };
};

export const calculateRevenueFee = (
  depositDetails: Array<{ creditCardFee?: number | string }> = []
) => {
  if (!depositDetails.length) return 0;

  return depositDetails.reduce((totalFee, item) => {
    return totalFee + Number(item.creditCardFee || 0);
  }, 0);
};

export const getWorkorderMechanic = async ({ technicians }) => {
  try {
    const url = `${getMechanicUrl()}?code=${technicians.primary_technician_number || ""
      }`;
    const response = await fleetlogicAPI.get(url, {
      headers: { "Content-Type": "application/json" }
    });
    const mechanic = head(response.data.results) as {
      name: string;
      id: number;
      srm_uid: string;
    };
    if (mechanic) {
      const selected = [
        {
          label: mechanic.name,
          value: mechanic.id,
          id: mechanic.id,
          srm_uid: mechanic.srm_uid
        }
      ];
      return { selectedMechanics: selected, error: null };
    } else {
      return { selectedMechanics: [], error: "Mechanic not found" };
    }
  } catch (error) {
    return { selectedMechanics: [], error: getApiErrorMsg(error) };
  }
};

export enum PlaceType {
  DIVISION = "Division",
  LOCATION = "Location"
}

export function filterSortMapOptions<T, R = T>(
  options: T[],
  search: string,
  filterFields: (keyof T)[],
  sortField: keyof T,
  numericSort: boolean = false,
  labelMapper?: (option: T) => R
) {
  let filtered = [...options];
  if (search && search.trim() !== "") {
    const searchLower = search.trim().toLowerCase();
    filtered = options.filter(option =>
      filterFields.some(field => {
        const val = option[field];
        if (val === undefined || val === null) return false;
        return val.toString().toLowerCase().includes(searchLower);
      })
    );
  }
  const sorted = filtered.sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (!aVal && !bVal) return 0;
    if (!aVal) return -1;
    if (!bVal) return 1;
    if (numericSort) {
      const aNum = /^\d+$/.test(aVal as string)
        ? parseInt(aVal as string, 10)
        : NaN;
      const bNum = /^\d+$/.test(bVal as string)
        ? parseInt(bVal as string, 10)
        : NaN;
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      if (!isNaN(aNum)) return -1;
      if (!isNaN(bNum)) return 1;
    }
    return aVal.toString().localeCompare(bVal.toString());
  });
  return labelMapper ? sorted.map(labelMapper) : (sorted as any[]);
}

export const isGridApiLoadedForViewsAndExport = (gridApi: any) => {
  return (
    gridApi &&
    gridApi?.getToolPanelInstance &&
    gridApi?.setFilterModel &&
    gridApi?.applyColumnState
  );
};
